import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../lib/apiClient.js';

interface HealthResponse {
  status: string;
  environment: string;
  version: string;
  timestamp: string;
}

export function HealthCheck() {
  const [data, setData] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<HealthResponse>('/api/v1/health')
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setRequestId(res.requestId);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setError(`${err.code}: ${err.message}`);
          setRequestId(err.requestId);
        } else {
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="mx-auto max-w-2xl px-4 py-16">
      <h1 className="text-2xl font-bold">API health</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
        Live round-trip to the API. Useful as a manual smoke test that the whole
        Pages → Workers → response chain is wired up.
      </p>

      {data && (
        <dl className="mt-6 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="font-medium">Status</dt>
          <dd>{data.status}</dd>
          <dt className="font-medium">Environment</dt>
          <dd>{data.environment}</dd>
          <dt className="font-medium">Version</dt>
          <dd className="font-mono">{data.version}</dd>
          <dt className="font-medium">Timestamp</dt>
          <dd className="font-mono">{data.timestamp}</dd>
        </dl>
      )}

      {error && (
        <div role="alert" className="mt-6 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          <p className="font-medium">Error</p>
          <p className="mt-1">{error}</p>
        </div>
      )}

      {requestId && (
        <p className="mt-6 text-xs text-gray-500 dark:text-gray-500">
          requestId: <span className="font-mono">{requestId}</span>
        </p>
      )}
    </section>
  );
}
